/**
 * The phone website's messages, in the shape the shared chat screen draws (shared-client-screens
 * D3, ClientChatView): the tab's one chat (`WebChatHub`) under one filter.
 *
 * - The entries are the filter's stream, oldest first, unsent bubbles included; each entry's room
 *   (Secure or Telegram) gives its tick and its mark (M7). A notice's door with no website page (an
 *   invite) is dropped, so it shows no action (webDoorHref).
 * - The rest gate (as useClientChatThread's, M14): while the client is scrolled up and moving the
 *   thread, what the hub brings at the newest end waits and goes on screen at rest or on a pin;
 *   older history joins at once. While it waits the hub marks nothing read; at rest it does
 *   (Codex review 2026-10-26).
 * - Typing shows only under the filters holding the studio's secure messages (M12); a first page
 *   the server answered 404 is not found (M10).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ClientThreadEntry } from '@telify/shared';
import type { ChatThreadGate } from '@/hooks/chat/useChatThreadScroll';
import type { ClientSendIssue } from '@/lib/client/clientChatThread';
import type { ChatMark } from '@/pages/ClientChatScreen/ClientChatTimeline';

import type { FilterView } from './chatHub';
import { webDoorHref } from './messageDoors';
import { webFilterShowsTyping, type WebMessageFilter } from './messageFilters';
import { compareStreamItems, type StreamItem } from './messageStream';
import { useWebChat } from './WebChat';

export interface WebChatThread {
  readonly status: FilterView['status'];
  readonly entries: ClientThreadEntry[];
  readonly secureOf: (entry: ClientThreadEntry) => boolean;
  readonly markOf: (entry: ClientThreadEntry) => ChatMark;
  readonly issueOf: (entry: ClientThreadEntry) => ClientSendIssue | null;
  readonly hasOlder: boolean;
  readonly loadOlder: () => void;
  /** Shows the bubble and sends it; returns its key. */
  readonly send: (text: string) => string;
  readonly retrySend: (key: string) => void;
  /** Try again after the messages could not be read. */
  readonly retry: () => void;
  readonly typing: boolean;
  readonly notifyTyping: () => void;
}

/** While the gate is held: the shown stream, with any older history the hub brought joined above it. */
function joinOlder(shown: readonly StreamItem[], latest: readonly StreamItem[]): readonly StreamItem[] {
  if (shown.length === 0) return latest;
  const first = shown[0];
  const older = latest.filter((item) => compareStreamItems(item, first) < 0);
  return older.length > 0 ? [...older, ...shown] : shown;
}

/** A notice whose door has no page on this website shows no action. */
function entryOf({ entry }: StreamItem): ClientThreadEntry {
  if (entry.kind !== 'automatic' || !entry.payload.clientDoor || webDoorHref(entry.payload.clientDoor)) return entry;
  return { ...entry, payload: { ...entry.payload, clientDoor: null } };
}

export function useWebChatThread(filter: WebMessageFilter, gate: ChatThreadGate): WebChatThread {
  const { hub, state } = useWebChat();
  useEffect(() => hub.use(filter), [hub, filter]);
  const view = hub.viewOf(filter);

  // What is on screen: the hub's stream, or while the gate is held the stream as it was.
  const [shown, setShown] = useState({ filter, source: view.items, items: view.items });
  let current = shown;
  if (shown.filter !== filter || shown.source !== view.items) {
    const items = shown.filter === filter && gate.held() ? joinOlder(shown.items, view.items) : view.items;
    current = { filter, source: view.items, items };
    setShown(current);
  }
  useEffect(() => {
    hub.holdReads(() => gate.held());
    const off = gate.onRest(() => {
      setShown((now) => (now.items === now.source ? now : { ...now, items: now.source }));
      hub.readsReleased();
    });
    return () => {
      off();
      hub.holdReads(null);
    };
  }, [gate, hub]);

  const { items } = current;
  const entries = useMemo(() => items.map(entryOf), [items]);
  const rooms = useMemo(() => new Map(items.map((item) => [item.entry.id, item.room])), [items]);
  const secureOf = useCallback((entry: ClientThreadEntry) => rooms.get(entry.id) === 'secure', [rooms]);
  const markOf = useCallback((entry: ClientThreadEntry): ChatMark => (rooms.get(entry.id) === 'secure' ? 'secure' : 'telegram'), [rooms]);
  const { issues } = state;
  const issueOf = useCallback(
    (entry: ClientThreadEntry) => (entry.kind === 'chat-message' ? issues.get(entry.id) ?? null : null),
    [issues],
  );

  return {
    status: view.status,
    entries,
    secureOf,
    markOf,
    issueOf,
    hasOlder: view.hasOlder,
    loadOlder: useCallback(() => void hub.loadOlder(), [hub]),
    send: useCallback((text: string) => hub.send(text), [hub]),
    retrySend: useCallback((key: string) => hub.retrySend(key), [hub]),
    retry: useCallback(() => hub.retryLoad(), [hub]),
    typing: state.typing && webFilterShowsTyping(filter),
    notifyTyping: useCallback(() => hub.notifyTyping(), [hub]),
  };
}
